import { z } from 'zod';
import { type Gem, gemKeyOf } from '../gemRows.js';
import { type Metric, metricsOf, type ResultRow, rowsOf } from '../resultRows.js';

const HOOK_SCENE = 'Hook Scene';

const featureSchema = z.object({ feature: z.string(), scene: z.string() });
const elementSchema = z.looseObject({
  feature: z.string(),
  tag: z.string(),
  scene: z.string(),
  network: z.string(),
  carriers: z.number(),
  spend_share: z.number(),
});
const viewSchema = z.object({
  key: z.string(),
  label: z.string(),
  is_rest: z.boolean(),
  spend: z.looseObject({ metric: z.string() }),
  kpi: z.looseObject({ metric: z.string() }),
  creative_count: z.number(),
  flags: z.array(z.string()),
  top_spender_ids: z.array(z.string()),
  top_performer_ids: z.array(z.string()),
  fatigued: z.array(z.looseObject({ creative_id: z.string() })),
  elements: z.array(elementSchema),
});

export const comparisonResultSchema = z.object({
  breakdown_label: z.string(),
  views: z.array(viewSchema),
  recommended_feature: featureSchema.nullable(),
  creatives: z.array(z.looseObject({ creative_id: z.string() })),
});

export type ComparisonResult = z.infer<typeof comparisonResultSchema>;
export type ComparisonView = ComparisonResult['views'][number];
type ComparisonElement = ComparisonView['elements'][number];

export interface ViewRows {
  spenders: ResultRow[];
  performers: ResultRow[];
  fatigued: ResultRow[];
}

export interface PickedElement {
  feature: string;
  tag: string;
  scene: string;
  network: string;
}

/** One tag across the views: each view's own reading of it, or null where it lacks it. */
export interface TagRow {
  key: string;
  gems: (Gem | null)[];
}

export function featureKeyOf(feature: { feature: string; scene: string }): string {
  return `${feature.scene}|${feature.feature}`;
}

/** Every feature either view has tags for, keyed like the recommendation. */
export function featureOptionsOf(
  result: ComparisonResult,
): { id: string; feature: string; scene: string }[] {
  const seen = new Map<string, { id: string; feature: string; scene: string }>();
  for (const element of result.views.flatMap((view) => view.elements)) {
    const id = featureKeyOf(element);
    if (!seen.has(id)) seen.set(id, { id, feature: element.feature, scene: element.scene });
  }
  return [...seen.values()];
}

/** A view's tag reads as an element-list gem, so the element-list card renders it. */
export function gemOf(element: ComparisonElement): Gem {
  return {
    key: gemKeyOf(element),
    feature: element.feature,
    tag: element.tag,
    scene: element.scene,
    network: element.network,
    creativeCount: element.carriers,
    potential: null,
    spendShare: element.spend_share,
    metrics: metricsOf(element),
  };
}

export function tagsOf(view: ComparisonView, featureKey: string): Gem[] {
  return view.elements.filter((element) => featureKeyOf(element) === featureKey).map(gemOf);
}

export function hookScenesOf(view: ComparisonView): Gem[] {
  return view.elements.filter((element) => element.scene === HOOK_SCENE).map(gemOf);
}

/** Lines the views' tags up by the service's row key, in the order they first appear. */
export function tagRowsOf(perView: Gem[][]): TagRow[] {
  const keys = [...new Set(perView.flat().map((gem) => gem.key))];
  return keys.map((key) => ({
    key,
    gems: perView.map((gems) => gems.find((gem) => gem.key === key) ?? null),
  }));
}

export function overviewOf(view: ComparisonView): { spend: Metric | null; kpi: Metric | null } {
  const [spend, kpi] = metricsOf({ metrics: [view.spend, view.kpi] });
  return { spend: spend ?? null, kpi: kpi ?? null };
}

/**
 * A view lists ids into the shared pool, and a selection is a pool row: the
 * service matches picks against that list. Fatigued rows keep their own values
 * for the fatigue deltas but take the pool row's id.
 */
export function viewRowsOf(view: ComparisonView, pool: ResultRow[]): ViewRows {
  const byCreative = new Map(pool.map((row) => [row.creativeId, row]));
  const join = (ids: string[]): ResultRow[] =>
    ids.flatMap((id) => {
      const row = byCreative.get(id);
      return row === undefined ? [] : [row];
    });
  const fatigued = rowsOf({ creatives: view.fatigued }, 'comparison').flatMap((row) => {
    const pooled = byCreative.get(row.creativeId);
    return pooled === undefined ? [] : [{ ...row, id: pooled.id }];
  });
  return {
    spenders: join(view.top_spender_ids),
    performers: join(view.top_performer_ids),
    fatigued,
  };
}

export function pickedElementsOf(result: ComparisonResult, keys: string[]): PickedElement[] {
  const picked = new Map<string, PickedElement>();
  for (const element of result.views.flatMap((view) => view.elements)) {
    const key = gemKeyOf(element);
    if (keys.includes(key) && !picked.has(key)) {
      picked.set(key, {
        feature: element.feature,
        tag: element.tag,
        scene: element.scene,
        network: element.network,
      });
    }
  }
  return [...picked.values()];
}
